// frontend/src/components/ExpenseManagement/index.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNexusRefresh } from '../../context/NexusSyncContext';
import { useAuth } from '../Auth';
import ExpenseList from './ExpenseList';
import ExpenseForm from './ExpenseForm';
import { apiService } from '../../services/api'; // Importer apiService
import { PlusCircle, Filter, RefreshCw, Loader2, ChevronDown, ChevronUp } from 'lucide-react';

const ExpenseManagement = () => {
  const [expenses, setExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(true); // État de chargement spécifique aux dépenses
  const [loadingInitialData, setLoadingInitialData] = useState(true); // État pour le chargement initial (projets, employé)
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null); // État pour les messages de succès
  const [showAddForm, setShowAddForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(15);
  const [totalItems, setTotalItems] = useState(0);
  const [filters, setFilters] = useState({ employee_id: null, project_id: null });
  const [projects, setProjects] = useState([]);
  const [employeeId, setEmployeeId] = useState(null); // ID de l'employé connecté
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showFiltersBar, setShowFiltersBar] = useState(false);
  const { user } = useAuth(); // Récupérer l'utilisateur connecté pour son ID et vérifier si admin

  const localApiBaseUrl = apiService.getBaseUrl(); // Obtenir l'URL de base de l'API

  // Fonction pour afficher/masquer les messages d'erreur/succès
  const displayMessage = useCallback((setter, message, duration = 4000) => {
    setter(message);
    setTimeout(() => setter(null), duration);
  }, []);

  // Charger les données initiales (projets, employé)
  const loadInitialData = useCallback(async () => {
    setLoadingInitialData(true);
    setError(null); // Reset error on initial load
    try {
      const [projectsData, employeesData] = await Promise.all([
        apiService.get('/projects?include_all=true').catch(() => []),
        apiService.get('/employees').catch(() => [])
      ]);

      setProjects(Array.isArray(projectsData) ? projectsData : []);

      if (user?.email && Array.isArray(employeesData)) {
        const currentEmployee = employeesData.find(emp => emp.email.toLowerCase() === user.email.toLowerCase());
        setEmployeeId(currentEmployee?.id || null);
        if (!currentEmployee) {
          console.warn("Aucun employé correspondant trouvé pour l'utilisateur connecté.");
          // Optionnel: Afficher une erreur si l'ID employé est crucial et non trouvé
          // displayMessage(setError, "Impossible de lier votre compte à un employé enregistré.");
        }
      } else {
        setEmployeeId(null);
      }
    } catch (err) {
      console.error('Erreur chargement données initiales:', err);
      const errorMsg = `Erreur chargement données initiales: ${err.message || 'Inconnue'}`;
      displayMessage(setError, errorMsg);
    } finally {
      setLoadingInitialData(false);
    }
  }, [user, displayMessage]); // Ajouter displayMessage aux dépendances

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  // Charger les dépenses (déclenché après le chargement initial ou par filtres/pagination)
  const loadExpenses = useCallback(async (page = currentPage, currentFilters = filters) => {
    if (loadingInitialData) return; // Attendre que les données initiales soient chargées

    // Déterminer si on peut charger les dépenses
    const isAdminUser = user?.is_admin ?? false; // Assumer non admin si user n'est pas défini
    const hasValidEmployeeId = employeeId !== null;
    const hasNoEmployeeFilter = currentFilters.employee_id === null;
    const canLoad = (isAdminUser && hasNoEmployeeFilter) || hasValidEmployeeId;

     // Si non admin et pas d'ID employé trouvé, ne rien charger et potentiellement afficher un message
     if (!isAdminUser && !hasValidEmployeeId) {
        setExpenses([]);
        setTotalItems(0);
        setLoadingExpenses(false);
        // Ne pas afficher d'erreur ici, loadInitialData s'en charge peut-être déjà
        return;
     }

    setLoadingExpenses(true);
    setIsRefreshing(true);
    setError(null); // Reset error before fetching
    try {
      let url = `/expenses?page=${page}&size=${pageSize}`;
      const params = new URLSearchParams();
      // Appliquer le filtre employé seulement s'il est défini
      const effectiveEmployeeIdFilter = isAdminUser ? currentFilters.employee_id : employeeId; // L'admin filtre via UI, l'user par son ID
      if (effectiveEmployeeIdFilter) params.append('employee_id', effectiveEmployeeIdFilter);
      if (currentFilters.project_id) params.append('project_id', currentFilters.project_id);

      const filterString = params.toString();
      if (filterString) url += `&${filterString}`;

      const data = await apiService.get(url);
      setExpenses(data.items || []);
      setTotalItems(data.total || 0);
    } catch (err) {
      const errorMsg = `Erreur chargement dépenses: ${err.message || 'Inconnue'}`;
      displayMessage(setError, errorMsg); // Utiliser displayMessage
      setExpenses([]);
      setTotalItems(0);
    } finally {
      setLoadingExpenses(false);
      setIsRefreshing(false);
    }
  }, [currentPage, filters, pageSize, employeeId, user, loadingInitialData, displayMessage]); // Ajouter user, loadingInitialData, displayMessage

  useEffect(() => {
    if (!loadingInitialData) {
      loadExpenses();
    }
  }, [loadExpenses, loadingInitialData]);

  useNexusRefresh(['expenses'], () => loadExpenses());

  // --- Handlers ---
  const handleAddExpense = async (formData) => {
    try {
      await apiService.uploadFile('/expenses', formData); // uploadFile gère FormData et méthode POST par défaut
      setShowAddForm(false);
      loadExpenses(1, filters);
      displayMessage(setSuccessMessage, "Dépense ajoutée avec succès !");
      setError(null);
    } catch (err) {
      console.error(`Erreur ajout dépense:`, err);
      const errMsg = `Erreur ajout: ${err?.message || (err && err.toString ? err.toString() : 'Inconnue')}`;
      displayMessage(setError, errMsg);
      throw err;
    }
  };

  const handleUpdateExpense = useCallback(async (expenseId, dataToUpdate) => {
    let updatedExpense;
    try {
      if (dataToUpdate instanceof FormData) {
        // Si FormData (contient un fichier), utiliser uploadFile avec PATCH
        updatedExpense = await apiService.uploadFile(`/expenses/${expenseId}`, dataToUpdate, null, 'PATCH');
      } else {
        // Si JSON (pas de fichier), utiliser patch standard
        updatedExpense = await apiService.patch(`/expenses/${expenseId}`, dataToUpdate);
      }

      // Mettre à jour l'état local SANS recharger toute la liste
      setExpenses(prevExpenses =>
        prevExpenses.map(exp =>
          exp.id === expenseId ? { ...exp, ...updatedExpense } : exp // Fusionner les données mises à jour
        )
      );
      displayMessage(setSuccessMessage, "Dépense mise à jour avec succès !");
      setError(null);
      return updatedExpense;
    } catch (err) {
      console.error(`Erreur MAJ dépense ${expenseId}:`, err);
      const errorMsg = `Erreur MAJ: ${err?.message || 'Inconnue'}`;
      displayMessage(setError, errorMsg); // Utiliser displayMessage
      throw err;
    }
  }, [displayMessage]); // displayMessage ajouté

  const handleDelete = async (id) => {
    if (window.confirm('Supprimer cette dépense ? Cette action est irréversible.')) {
      try {
        await apiService.delete(`/expenses/${id}`);
        displayMessage(setSuccessMessage, "Dépense supprimée avec succès.");
        // Recharger la page courante pour refléter la suppression
        loadExpenses(currentPage, filters);
      } catch (err) {
        console.error('Erreur suppression:', err);
        const errorMsg = `Erreur suppression: ${err?.message || 'Inconnue'}`;
        displayMessage(setError, errorMsg);
      }
    }
  };

  const handlePageChange = (page) => {
      setCurrentPage(page);
      // loadExpenses sera appelé par le useEffect dépendant de currentPage
  };

  const handleFilterChange = (newFilters) => {
      setFilters(newFilters);
      setCurrentPage(1); // Revenir à la page 1 avec les nouveaux filtres
      // loadExpenses sera appelé par le useEffect dépendant de filters
  };


  // Déterminer si l'utilisateur peut ajouter des dépenses (doit avoir un employeeId)
  const canAddExpense = employeeId !== null;

  return (
    <div className="p-4 md:p-6 lg:p-8 space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-800">Gestion des Dépenses</h1>
        <div className="flex items-center space-x-2">
          <button onClick={() => loadExpenses(currentPage, filters)} disabled={isRefreshing || loadingExpenses} className="p-2 rounded-full text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors disabled:opacity-50 disabled:cursor-wait" title="Rafraîchir">
            <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          {/* Afficher le bouton seulement si l'employé peut ajouter */}
          {canAddExpense && (
            <button onClick={() => setShowAddForm(true)} disabled={showAddForm} className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-2 px-4 rounded-lg shadow hover:shadow-md flex items-center transition duration-150 ease-in-out disabled:opacity-70 disabled:cursor-not-allowed">
              <PlusCircle className="h-5 w-5 mr-2" /> Nouvelle dépense
            </button>
          )}
          {/* Afficher un message si l'employé ne peut pas ajouter (et n'est pas admin) */}
          {!loadingInitialData && !canAddExpense && !(user && user.is_admin) && (
            <div className="text-sm text-orange-600 italic px-4 py-2 bg-orange-50 rounded border border-orange-200">
              Votre compte utilisateur n'est pas lié à un employé enregistré.
            </div>
          )}
        </div>
      </div>

      {/* Affichage des messages succès/erreur */}
      {error && !loadingExpenses && ( <div className="my-4 p-3 bg-red-100 text-red-700 rounded border border-red-200 text-sm">{error}</div> )}
      {successMessage && ( <div className="my-4 p-3 bg-green-100 text-green-700 rounded border border-green-200 text-sm">{successMessage}</div> )}

      {/* Formulaire d'Ajout (si actif et possible) */}
      {showAddForm && canAddExpense && (
        <ExpenseForm
          key={`add-form-${employeeId}`} // Clé peut aider à réinitialiser si employeeId changeait
          onSubmit={handleAddExpense}
          projects={projects}
          employeeId={employeeId}
          onCancel={() => setShowAddForm(false)}
          apiBaseUrl={localApiBaseUrl}
        />
      )}

      {/* Filtres */}
       <div className="mb-4">
          <button
            onClick={() => setShowFiltersBar(!showFiltersBar)}
            className="flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 mb-2 bg-white px-3 py-1.5 rounded-md border shadow-sm hover:bg-gray-50"
          >
            <Filter className="w-4 h-4 mr-1.5" />
            {showFiltersBar ? 'Masquer les filtres' : 'Afficher les filtres'}
            {showFiltersBar ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
          {showFiltersBar && (
            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Filtrer par Projet</label>
                  <select name="project_id" value={filters.project_id || ''} onChange={(e) => handleFilterChange({ ...filters, project_id: e.target.value === '' ? null : e.target.value })} className="w-full p-2 border border-gray-300 rounded-md bg-white text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500">
                    <option value="">Tous</option>
                    {projects.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
                  </select>
                </div>
                 { (user && user.is_admin) && ( // Condition pour admin
                     <div>
                       <label className="block text-xs font-medium text-gray-700 mb-1">Filtrer par Employé (ID)</label>
                       <input type="number" name="employee_id" value={filters.employee_id || ''} onChange={(e) => handleFilterChange({ ...filters, employee_id: e.target.value === '' ? null : e.target.value })} placeholder="ID" className="w-full p-2 border border-gray-300 rounded-md text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
                    </div>
                 )}
              </div>
               <div className="mt-3 flex justify-end">
                  <button onClick={() => handleFilterChange({ project_id: null, employee_id: user?.is_admin ? null : employeeId })} className="text-xs text-gray-500 hover:text-gray-700 hover:underline">Réinitialiser</button>
               </div>
            </div>
          )}
      </div>

      {/* Liste des Dépenses */}
      <ExpenseList
        expenses={expenses}
        loading={loadingExpenses || isRefreshing}
        projects={projects}
        onDelete={handleDelete}
        onUpdate={handleUpdateExpense}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={handlePageChange}
        filters={filters}
        onFilterChange={handleFilterChange} // Garder pour le composant filtre si besoin
        isAdmin={user && user.is_admin} // Adapter si besoin
        currentUserId={employeeId} // ID de l'employé connecté
        apiBaseUrl={localApiBaseUrl}
      />
    </div>
  );
};

export default ExpenseManagement;
